import { cn } from "@/lib/utils";
import { Check } from "lucide-react";

/** Classes partagées pour les onglets actifs/inactifs (dark theme) */
export const TAB_TRIGGER_CLASSES = [
  "data-[state=active]:bg-white/10",
  "data-[state=active]:text-white",
  "data-[state=active]:font-semibold",
  "data-[state=active]:border-b-2",
  "data-[state=active]:border-emerald-500",
  "data-[state=active]:rounded-t-lg",
  "text-gray-400",
  "hover:text-gray-200",
  "hover:bg-white/5",
  "transition-all",
  "duration-200",
].join(" ");

const DOT_COLORS = {
  emerald: "bg-emerald-500",
  orange: "bg-orange-500",
  red: "bg-red-500",
  purple: "bg-purple-500",
  gray: "bg-gray-500",
} as const;

interface TabBadgeProps {
  /** Compteur numérique (ex: 3 devis). Masqué si 0 ou undefined. */
  count?: number;
  /** Couleur du dot de statut. Masqué si undefined. */
  dotColor?: keyof typeof DOT_COLORS;
  /** Animation pulse sur le dot (ex: Analyse IA disponible). */
  pulse?: boolean;
  /** Label custom (ex: "+5j", "12%"). Remplace le compteur si fourni. */
  label?: string;
  /** Classe de fond pour le label pill. Défaut: bg-white/15. */
  labelBgClass?: string;
  /** Classe de couleur texte pour le label pill. */
  labelTextClass?: string;
  /** Masquer le compteur/label sur mobile (< sm). Le dot reste visible. */
  hideCountOnMobile?: boolean;
  /** Afficher un checkmark vert validé (ex: paiement 100%). */
  checkmark?: boolean;
}

export function TabBadge({
  count,
  dotColor,
  pulse,
  label,
  labelBgClass,
  labelTextClass,
  hideCountOnMobile,
  checkmark,
}: TabBadgeProps) {
  const hasCount = count != null && count > 0;
  const hasDot = !!dotColor;
  const hasLabel = !!label;

  if (!hasCount && !hasDot && !hasLabel && !checkmark) return null;

  return (
    <>
      {/* Checkmark validé (ex: paiement 100%) */}
      {checkmark && (
        <span className="ml-1.5 inline-flex items-center justify-center w-[18px] h-[18px] rounded-full bg-emerald-500/20">
          <Check className="w-3 h-3 text-emerald-400" strokeWidth={3} />
        </span>
      )}

      {/* Compteur pill */}
      {!checkmark && hasCount && !hasLabel && (
        <span
          className={cn(
            "ml-1.5 items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-white/15 text-[10px] font-bold leading-none",
            hideCountOnMobile ? "hidden sm:inline-flex" : "inline-flex"
          )}
        >
          {count}
        </span>
      )}

      {/* Label pill (ex: "+5j", "30%") */}
      {!checkmark && hasLabel && (
        <span
          className={cn(
            "ml-1.5 items-center px-1.5 h-[18px] rounded-full text-[10px] font-bold leading-none",
            hideCountOnMobile ? "hidden sm:inline-flex" : "inline-flex",
            labelBgClass || "bg-white/15",
            labelTextClass
          )}
        >
          {label}
        </span>
      )}

      {/* Dot de statut */}
      {!checkmark && hasDot && (
        <span
          className={cn(
            "ml-1 w-2 h-2 rounded-full shrink-0",
            DOT_COLORS[dotColor],
            pulse && "animate-pulse"
          )}
        />
      )}
    </>
  );
}
